import { Survey, SurveyChoice } from "@kalyzee/kast-web-sdk/websocket";
import React, { useMemo } from "react";
import { hslToHex } from "../../helpers/colors";
import { arrayShuffleInPlace } from "../../helpers/utils";
import { ProgressBar, StarRating } from "@kalyzee/kalyzee-ui";

export interface SurveyResultsViewerColors {
  background: string;
  fill: string;
  text: string;
  subText: string;
}

const COLORS: SurveyResultsViewerColors[] = Array.from({ length: 18 }).map((_, idx) => {
  const hue = idx * 20; // Generates hues from 0 to 340 in steps of 20
  return {
    background: hslToHex(hue, 1, 0.95),
    fill: hslToHex(hue, 1, 0.8),
    text: hslToHex(hue, 0.25, 0.5),
    subText: hslToHex(hue, 0.25, 0.5),
  };
});

const STARS_COLORS: SurveyResultsViewerColors = {
  background: "rgba(0, 144, 255, 0.2)",
  fill: "rgb(0, 144, 255)",
  text: "rgb(0, 144, 255)",
  subText: "rgb(0, 144, 255)",
};

export interface SurveyResultsViewerProps {
  survey?: Survey;
  customColors?: {
    normal?: SurveyResultsViewerColors[];
    stars?: SurveyResultsViewerColors;
  };
}

const SurveyProgressBar = React.memo(({
  index,
  content,
  percentage,
  votesNumber,
  colors,
  starMode,
  verticalMode,
}: {
  index: number;
  content: string;
  percentage: number;
  votesNumber: number;
  colors: SurveyResultsViewerColors;
  starMode: boolean;
  verticalMode: boolean;
}) => (
  <ProgressBar
    key={index}
    title={starMode ? { mode: "stars", value: parseFloat(content) } : { mode: "text", value: content }}
    percentage={percentage}
    number={votesNumber}
    colors={colors}
    orientation={verticalMode ? "horizontal" : "vertical"}
    style={starMode ? { fontSize: "14px" } : undefined}
  />
));


export const SurveyResultsViewer = React.memo(({ survey, customColors }: SurveyResultsViewerProps) => {
  const data = useMemo(() => {
    if (!survey?.questions) return undefined;
    const question = survey.questions[0];
    if (!question) return undefined; // Only one question supported for now

    const questionVoteNb = question.choices.reduce((acc, val) => {
      if (val.votesNumber) return acc + val.votesNumber;
      return acc;
    }, 0);

    let randomColors: SurveyResultsViewerColors[] | undefined;
    let idx = 0;
    const getRandomColor = () => {
      if (!randomColors || idx === randomColors.length) {
        randomColors = [...(customColors?.normal || COLORS)];
        arrayShuffleInPlace(randomColors);
        idx = 0;
      }
      const color = randomColors[idx];
      idx++;
      return color;
    };

    const isStar = survey.name === "Étoiles";
    return {
      totalVotes: questionVoteNb,
      choices: question.choices.map((choice, index) => {
        const colors = isStar ? customColors?.stars ?? STARS_COLORS : getRandomColor();
        return {
          id: choice.id,
          content: choice.content,
          votesNumber: choice.votesNumber ?? 0,
          isStar,
          colors,
        };
      }),
    };
  }, [survey]);

  const starMode = survey?.name === "Étoiles";
  const verticalMode = starMode;

  const renderAverageStars = () => {
    if (!data?.choices) return null;
    const average =
      data.choices.reduce((p, curr) => {
        return p + parseFloat(curr.content) * curr.votesNumber;
      }, 0) / (data.totalVotes || 1);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          gap: "50px",
          marginBottom: "30px",
          fontSize: "2em",
        }}
      >
        <StarRating
          allowHalfStars
          showHoverPreview={false}
          value={average}
          starColors={{ full: customColors?.stars?.fill ?? STARS_COLORS.fill, empty: customColors?.stars?.background ?? STARS_COLORS.background }}
          starStyle={{ width: "1em" }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            color: customColors?.stars?.text ?? STARS_COLORS.text,
            paddingTop: "2px",
          }}
        >
          <b style={{ marginRight: "8px" }}>{average.toFixed(1)}</b> /5
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          overflowX: "auto",
          gap: "1.25rem",
          padding: "1em",
          width: "100%",
          flexDirection: verticalMode ? "column" : "row",
          //justifyContent: "center",
        }}
      >
        {starMode ? renderAverageStars() : null}
        {data?.choices.map((choice, index) => {
          return (
            <div
              key={`choice-${choice.id}`}
              style={{
                height: verticalMode ? undefined : "100%",
                width: verticalMode ? "100%" : undefined,
                minWidth: verticalMode ? undefined : "fit-content",
                flex: 1,
              }}
            >
              <SurveyProgressBar
                index={index}
                content={choice.content}
                percentage={data.totalVotes > 0 ? Math.round((choice.votesNumber / data.totalVotes) * 100) : 0}
                votesNumber={choice.votesNumber}
                colors={choice.colors}
                starMode={starMode}
                verticalMode={verticalMode}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});
